/**
 * Responsibility, scope and data (CLAUDE.md 1.2, 2.1; REVIEW-001 P3-5).
 *
 * Public, and linked from the sign-in page, because a prospective user should meet
 * these three things *before* they sign up:
 *
 * 1. **The responsibility model** (2.1). CalcDesk calculates as instructed; the
 *    engineer designs. This is a liability position, not marketing copy.
 * 2. **The explicit non-goals** (1.2). "Engineers will assume anything unstated is
 *    included" -- so torsion, seismic, 3D frame analysis and the rest are listed
 *    plainly rather than left to be discovered.
 * 3. **What we store and what the user can do about it.**
 */
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/card'
import { LanguageToggle } from '@/components/ui/LanguageToggle'
import { ThemeToggle } from '@/components/ui/ThemeToggle'

/** The v1 non-goals table from CLAUDE.md 1.2, as i18n keys. */
const NON_GOALS = [
  'scope.seismic',
  'scope.frame',
  'scope.torsion',
  'scope.prestress',
  'scope.secondorder',
  'scope.fatigue',
  'scope.imperial',
  'scope.layout',
]

export function PrivacyPage() {
  const { t } = useTranslation()

  return (
    <div className="flex min-h-dvh flex-col bg-bg text-text">
      <header className="flex h-14 shrink-0 items-center border-b border-border px-4">
        <Link to="/" className="font-semibold">
          {t('app.name')}
        </Link>
        <div className="ms-auto flex items-center gap-1">
          <LanguageToggle />
          <ThemeToggle />
        </div>
      </header>

      <main className="mx-auto w-full max-w-2xl flex-1 space-y-4 p-4">
        <h1 className="text-lg font-semibold">{t('privacy.title')}</h1>

        <Card>
          <CardHeader>
            <CardTitle>{t('privacy.responsibility_title')}</CardTitle>
          </CardHeader>
          <CardBody>
            <p className="text-sm leading-relaxed">{t('privacy.responsibility_body')}</p>
          </CardBody>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('privacy.scope_title')}</CardTitle>
          </CardHeader>
          <CardBody>
            <ul className="list-inside list-disc space-y-1 text-sm">
              {NON_GOALS.map((key) => (
                <li key={key}>{t(key)}</li>
              ))}
            </ul>
          </CardBody>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('privacy.data_title')}</CardTitle>
          </CardHeader>
          <CardBody className="space-y-3">
            <p className="text-sm leading-relaxed">{t('privacy.data_body')}</p>
            <Button asChild>
              <Link to="/settings">{t('settings.privacy')}</Link>
            </Button>
          </CardBody>
        </Card>
      </main>
    </div>
  )
}
